import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CreditCard, CheckCircle, XCircle, Clock, Cpu, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const DevisPaiementPage = () => {
  const { devisId } = useParams();
  const [searchParams] = useSearchParams();
  const { t, tError } = useLanguage();
  const mode = searchParams.get('mode') || 'acompte';
  const [devis, setDevis] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDevis = async () => {
      try {
        const [devisRes, statusRes] = await Promise.all([
          axios.get(`${API}/api/devis/public/${devisId}`),
          axios.get(`${API}/api/devis/${devisId}/payment-status`).catch(() => null),
        ]);
        setDevis(devisRes.data);
        if (statusRes?.data) setPaymentStatus(statusRes.data);
      } catch (err) {
        setError(tError(err?.response?.data?.detail) || t('devisAccept.notFound'));
      } finally {
        setLoading(false);
      }
    };
    fetchDevis();
  }, [devisId]);

  const handlePay = async () => {
    setPaying(true);
    try {
      const res = await axios.post(`${API}/api/devis/${devisId}/pay?mode=${mode}`);
      const { checkout_url } = res.data;
      if (checkout_url) {
        window.location.href = checkout_url;
      } else {
        setError(t('payment.urlNotAvailable'));
        setPaying(false);
      }
    } catch (err) {
      setError(tError(err?.response?.data?.detail) || t('payment.creationError'));
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">{t('payment.error')}</h2>
          <p className="text-gray-400">{error}</p>
          <a href={`/devis/accepter/${devisId}`} className="inline-flex items-center gap-2 mt-4 text-violet-400 hover:text-violet-300">
            <ArrowLeft className="w-4 h-4" /> {t('payment.backToDevis')}
          </a>
        </div>
      </div>
    );
  }

  if (!devis || devis.status === 'en_attente') {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <Clock className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">{t('payment.notAccepted')}</h2>
          <p className="text-gray-400">{t('payment.mustAcceptFirst')}</p>
          <a href={`/devis/accepter/${devisId}`} className="inline-flex items-center gap-2 mt-4 text-violet-400 hover:text-violet-300">
            <ArrowLeft className="w-4 h-4" /> {t('payment.viewDevis')}
          </a>
        </div>
      </div>
    );
  }

  const total = parseFloat(devis.total) || 0;
  const alreadyPaid = paymentStatus ? parseFloat(paymentStatus.amount_paid || 0) : 0;
  const actualRemaining = total - alreadyPaid;
  const payAmount = mode === 'acompte' ? Math.min(Math.round(total * 50) / 100, actualRemaining) : actualRemaining;
  const isAcompte = mode === 'acompte';

  if (actualRemaining <= 0) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">{t('payment.fullyPaid')}</h2>
          <p className="text-gray-400">{t('payment.devis')} <strong className="text-violet-400">{devis.ref}</strong> {t('payment.alreadyPaid')}</p>
          <a href="/login" className="inline-flex items-center gap-2 mt-4 text-violet-400 hover:text-violet-300 text-sm">
            {t('payment.accessClient')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4" data-testid="devis-paiement-page">
      <div className="max-w-lg w-full space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center mb-4">
            <Cpu className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">R.E.Mobiles</h1>
          <p className="text-gray-400 text-sm">{t('payment.secure')}</p>
        </div>

        <div className="card-dark p-5 space-y-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-violet-400" />
            {isAcompte ? t('payment.deposit50') : t('payment.fullPayment')}
          </h2>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">{t('payment.devis')}</span>
              <span className="text-violet-400 font-semibold">{devis.ref}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">{t('payment.client')}</span>
              <span className="text-white">{devis.client_nom}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">{t('payment.totalDevis')}</span>
              <span className="text-white">{total.toFixed(2)} EUR</span>
            </div>
            {alreadyPaid > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-400">{t('payment.alreadyPaidAmount')}</span>
                <span className="text-green-400 font-semibold">-{alreadyPaid.toFixed(2)} EUR</span>
              </div>
            )}
            {alreadyPaid > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-400">{t('payment.remaining')}</span>
                <span className="text-amber-400 font-semibold">{actualRemaining.toFixed(2)} EUR</span>
              </div>
            )}
            {isAcompte && alreadyPaid === 0 && (
              <div className="flex justify-between">
                <span className="text-gray-400">{t('payment.depositPercent')}</span>
                <span className="text-amber-400 font-bold">{payAmount.toFixed(2)} EUR</span>
              </div>
            )}
          </div>

          <div className="border-t border-white/10 pt-3 flex justify-between items-center">
            <span className="font-bold text-white text-lg">{t('payment.amountToPay')}</span>
            <span className="text-2xl font-black text-green-400" data-testid="pay-amount">{payAmount.toFixed(2)} €</span>
          </div>
        </div>

        <div className="flex gap-2">
          <a href={`/devis/paiement/${devisId}?mode=acompte`}
            className={`flex-1 py-2 text-center rounded-lg text-sm font-semibold transition-colors ${
              isAcompte ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-white/5 text-gray-500 border border-white/10 hover:bg-white/10'
            }`} data-testid="mode-acompte-tab">
            {t('payment.deposit50')}
          </a>
          <a href={`/devis/paiement/${devisId}?mode=complet`}
            className={`flex-1 py-2 text-center rounded-lg text-sm font-semibold transition-colors ${
              !isAcompte ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-white/5 text-gray-500 border border-white/10 hover:bg-white/10'
            }`} data-testid="mode-complet-tab">
            {t('payment.fullPayment')}
          </a>
        </div>

        <button onClick={handlePay} disabled={paying}
          className="w-full py-4 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold text-lg flex items-center justify-center gap-3 transition-all disabled:opacity-50 shadow-lg shadow-green-500/20"
          data-testid="pay-now-btn">
          {paying ? (
            <><div className="loading-spinner w-5 h-5"></div> {t('payment.redirecting')}</>
          ) : (
            <><CreditCard className="w-6 h-6" /> {t('payment.pay')} {payAmount.toFixed(2)} €</>
          )}
        </button>

        <div className="text-center space-y-1">
          <p className="text-xs text-gray-500">{t('payment.securedBy')}</p>
          <p className="text-xs text-gray-600">R.E.Mobiles - 102 Avenue General de Gaulle, Porte 2, 97300 Cayenne</p>
        </div>
      </div>
    </div>
  );
};

export default DevisPaiementPage;
